/* elevation.js — per-wall front elevation: transform, membership, dimension chains,
 * drop/drag semantics and Edit-Position Elevation hooks (WI-6).
 *
 * Ported VERBATIM from lhc-app.html:
 *   getWallNumberMap (~33455) — spatial "Wall N" numbering (leftmost = 1)
 *   activeElevationWallId revalidation (~33291 / ~33936, LOCK-ELV-12)
 *   renderElevationSvg sizing + pads + _elevTransform (~35586–35652, LOCK-ELV-03/05)
 *   v0.38 elevation membership (a placement with a wallId IS on that wall, ~35528–35571)
 *   cabCy single-role mounting source (~35539: lhcRoleBaseY + height/2 + elevation)
 *   E1 aspect-fill camera + E2 viewBox reuse (~35611–35635), elevZoomBy clamps (~38184–38204)
 *   elevScreenToWorld inverse transform (~37072–37084, LOCK-ELV-06)
 *   clipPolygonByLine (~37090) + getWallElevationPolygon (~37116)
 *   dimension-chain point builders (~35786 dimMM, ~35819 role split, ~35822 _cabChainPts,
 *     ~35848/35924 opening chains, ~35868/35898/36056 cut chains, ~36030 unit-heights chain)
 *   CV detail parsers svgDoorCount/svgDrawerCount/svgHingeSide (~34112–34131)
 *   v0.42 elevation drop (place on the viewed wall at the dropped x, ~46577–46610)
 *   elevation drag-along-wall = the plan drag's same-wall semantics on the viewed wall
 *     (snap clamp + clampToContact per move ~37972–37994; commit validate/revert + magnet
 *     ~38014–38029) expressed in wall-local u
 *   Edit-Position Elevation tab values (~51877–51906, LOCK-EPE-01) and the absolute→delta
 *     elevation write (~52207–52227, LOCK-EPE-02: cfg.position.elevation stores the mm DELTA
 *     from the role base; geometry reads it additively at /1000)
 *
 * Units = METRES internally; millimetres appear only in labels / the props-tab boundary.
 * Mutating ops (elevationDropAt/elevationMoveTo/elevationCommitMove/setElevationOffFloorMm)
 * mutate the passed state/placement IN PLACE — engine shared-mutation semantics. Parity
 * beats elegance: do NOT modernize.
 */
import { DEFAULT_WALL_HEIGHT } from "./constants";
import { roleBaseY } from "./rules";
import { getWallPlacementInfo } from "./placement";
import { wallInnerSpan, snapSides } from "./snapping";
import { clampToContact, snapExactFit, snapExactFitEx } from "./contact";
import { stackElevationFor, applyStack } from "./stackLaw";
import { ruleAllowPlacement, ruleAdvise } from "./placementPipeline";
import { commitMove } from "./drag";
import { effDef } from "./pricing";
import { newPlacementId } from "./state";

export const ELEV_SCALE = 100; // px per metre (~35588)
export const ELEV_GAP = 10;    // px from wall face to first dim line (~35781)
export const ELEV_LANE = 18;   // px between wall/opening dim lines (~35782)
export const ELEV_SYM_LANE = 12; // px between symbol dim lines (~35787)
export const ELEV_EDGE_TOL = 0.05; // m (~35788)

/* ── Wall numbering + active-wall revalidation ────────────────────────────── */

/** getWallNumberMap(walls): Map<wallId, N> in SPATIAL order (leftmost x, then z, then
 *  creation index). Arc midpoints participate in the leftmost test. (~33455) */
export function getWallNumberMap(walls) {
  const src = walls || [];
  const annotated = src.map((w, idx) => {
    const mx = (w.midpoint && typeof w.midpoint[0] === "number") ? w.midpoint[0] : Infinity;
    const mz = (w.midpoint && typeof w.midpoint[1] === "number") ? w.midpoint[1] : Infinity;
    return { id: w.id, idx, minX: Math.min(w.start[0], w.end[0], mx), minZ: Math.min(w.start[1], w.end[1], mz) };
  });
  annotated.sort((a, b) => {
    if (Math.abs(a.minX - b.minX) > 0.01) return a.minX - b.minX;
    if (Math.abs(a.minZ - b.minZ) > 0.01) return a.minZ - b.minZ;
    return a.idx - b.idx;
  });
  const map = new Map();
  annotated.forEach((s, i) => map.set(s.id, i + 1));
  return map;
}

/** LOCK-ELV-12 (~33291): a deleted/renamed active wall falls back to walls[0] (or null). */
export function revalidateElevationWallId(currentId, walls) {
  if (!walls || walls.length === 0) return null;
  if (!walls.some((w) => w.id === currentId)) return walls[0].id;
  return currentId;
}

/* ── Transform (LOCK-ELV-03/05) ───────────────────────────────────────────── */

export function wallHeightOf(wall) {
  return (wall && wall.height) || DEFAULT_WALL_HEIGHT; // (~35526)
}

/** PAD/viewport sizing (~35586–35605). counts = { cabCount, opsCount, symsCount }.
 *  Returns the full _elevTransform superset: { wall, wallLen, wallH, SCALE, PAD_TOP,
 *  PAD_LEFT, PAD_RIGHT, PAD_BOTTOM, VW, VH }. */
export function computeElevTransform(wall, walls, counts) {
  const info = getWallPlacementInfo(wall, walls);
  const wallLen = info ? info.len : Math.hypot(wall.end[0] - wall.start[0], wall.end[1] - wall.start[1]);
  const wallH = wallHeightOf(wall);
  const SCALE = ELEV_SCALE;
  const _symsCount = (counts && counts.symsCount) || 0;
  const _opsCount = (counts && counts.opsCount) || 0;
  const _cabCount = (counts && counts.cabCount) || 0;
  const _aboveLayers = (_cabCount > 0 ? 1 : 0) + (_opsCount > 0 ? 1 : 0) + 1; // +1 total length always
  const PAD_TOP = Math.max(80, 10 + _aboveLayers * 20 + 18);
  const _rightLayers = (_opsCount > 0 ? 1 : 0) + 1; // +1 total height always
  const PAD_RIGHT = Math.max(60, 10 + _rightLayers * 20 + 18);
  const PAD_LEFT = Math.max(40, 10 + _symsCount * 12 + 14);
  const PAD_BOTTOM = Math.max(32, 10 + _symsCount * 12 + 10);
  const VW = wallLen * SCALE + PAD_LEFT + PAD_RIGHT;
  const VH = wallH * SCALE + PAD_TOP + PAD_BOTTOM;
  return { wall, wallLen, wallH, SCALE, PAD_TOP, PAD_LEFT, PAD_RIGHT, PAD_BOTTOM, VW, VH };
}

/** ex/ey — wall-local (u, h) metres → SVG user px (~35606–35609). */
export function exOf(t) { return (xm) => t.PAD_LEFT + xm * t.SCALE; }
export function eyOf(t) { return (ym) => t.PAD_TOP + (t.wallH - ym) * t.SCALE; }

/** Inverse: SVG user px → wall-local (u, h) metres — the pure core of
 *  elevScreenToWorld (~37081–37083). */
export function elevUserToWorld(ux, uy, t) {
  if (!t) return null;
  return [(ux - t.PAD_LEFT) / t.SCALE, t.wallH - (uy - t.PAD_TOP) / t.SCALE];
}

/** elevScreenToWorld(clientX, clientY, svgEl, t): screen px → (u, h) via getScreenCTM
 *  (LOCK-ELV-06, ~37072). DOM-dependent; returns null when the CTM is unavailable. */
export function elevScreenToWorld(clientX, clientY, svgEl, t) {
  if (!t || !svgEl || typeof svgEl.getScreenCTM !== "function") return null;
  const ctm = svgEl.getScreenCTM();
  if (!ctm) return null;
  const pt = svgEl.createSVGPoint();
  pt.x = clientX; pt.y = clientY;
  const inSvg = pt.matrixTransform(ctm.inverse());
  return elevUserToWorld(inSvg.x, inSvg.y, t);
}

/* ── E1/E2 viewBox camera ─────────────────────────────────────────────────── */

/** E-1 aspect-fill viewBox for a host of hostW×hostH px (~35617–35635). */
export function initialElevView(VW, VH, hostW, hostH) {
  const _ecw = hostW || VW;
  const _ech = hostH || VH;
  const _canvasAspect = _ecw / _ech;
  const _geomAspect = VW / VH;
  let vbW, vbH;
  if (_canvasAspect > _geomAspect) { vbH = VH; vbW = vbH * _canvasAspect; }
  else { vbW = VW; vbH = vbW / _canvasAspect; }
  return { x: (VW - vbW) / 2, y: (VH - vbH) / 2, w: vbW, h: vbH };
}

/** elevZoomBy viewBox mutation math (~38196–38204). inSvg = cursor in SVG user px.
 *  Returns the NEW view (or the same object when the clamp refuses). */
export function elevZoomView(view, factor, inSvgX, inSvgY, SCALE) {
  const minW = Math.max(50, SCALE ? 1.5 * SCALE : 100);
  const maxW = 30000;
  const targetW = view.w / factor;
  if (targetW < minW || targetW > maxW) return view;
  return {
    w: view.w / factor,
    h: view.h / factor,
    x: inSvgX - (inSvgX - view.x) / factor,
    y: inSvgY - (inSvgY - view.y) / factor,
  };
}

/* ── Wall silhouette (cuts) ───────────────────────────────────────────────── */

/** Sutherland-Hodgman line clip keeping the anchor's side (~37090). */
export function clipPolygonByLine(polygon, lineA, lineB, anchor) {
  const sideOf = (p) =>
    (lineB[0] - lineA[0]) * (p[1] - lineA[1]) -
    (lineB[1] - lineA[1]) * (p[0] - lineA[0]);
  const anchorSign = Math.sign(sideOf(anchor));
  if (anchorSign === 0) return polygon.slice();
  const out = [];
  for (let i = 0; i < polygon.length; i++) {
    const curr = polygon[i];
    const next = polygon[(i + 1) % polygon.length];
    const cs = sideOf(curr), ns = sideOf(next);
    const csSgn = Math.sign(cs), nsSgn = Math.sign(ns);
    if (csSgn === anchorSign || csSgn === 0) out.push([curr[0], curr[1]]);
    if (csSgn !== 0 && nsSgn !== 0 && csSgn !== nsSgn) {
      const t = cs / (cs - ns);
      out.push([curr[0] + t * (next[0] - curr[0]), curr[1] + t * (next[1] - curr[1])]);
    }
  }
  return out;
}

/** v0.40 elevation face: rectangle bounded by the INNER faces of connected walls,
 *  clipped by wall.elevCuts (~37116). walls needed for lhcWallInnerSpan. */
export function getWallElevationPolygon(wall, wallLen, wallH, walls) {
  const _fsp = wallInnerSpan(wall, walls || [wall]);
  let poly = [[_fsp.off0, 0], [wallLen - _fsp.off1, 0], [wallLen - _fsp.off1, wallH], [_fsp.off0, wallH]];
  if (wall.elevCuts && wall.elevCuts.length) {
    const anchor = [wallLen / 2, 0];
    for (const c of wall.elevCuts) {
      poly = clipPolygonByLine(poly, [c[0], c[1]], [c[2], c[3]], anchor);
      if (poly.length < 3) break;
    }
  }
  return poly;
}

/* ── Cabinet membership + vertical placement ──────────────────────────────── */

/** cabCy — v0.34/v0.38 single role source: roleBaseY + height/2 + per-placement
 *  elevation override (mm delta stored per LOCK-EPE-02, read /1000). (~35536–35539) */
export function elevCabY(p, def) {
  const _elev = (p.config && p.config.position && p.config.position.elevation)
    ? p.config.position.elevation / 1000 : 0;
  return roleBaseY(def) + def.height / 2 + _elev;
}

/** v0.38 ELEVATION MEMBERSHIP (~35528–35571): a placement with a wallId IS on that wall —
 *  period; kept when its center t is within ±0.05 of [0, wallLen]. Cabinets are hidden in
 *  the walls stage (~35531). Legacy clrLeft placements (no wallId, sofa-era) need the
 *  back-wall cursor fallback (getCabinetStartX/kitchenBackZ) — WI-13; skipped here like the
 *  rest of the extracted engine. Returns [{ def, axisPos, cabY, placementId }]. */
export function elevMembershipOnWall(state, lib, wall, walls, stage) {
  const out = [];
  const currentStage = stage || (state && state.currentStage);
  if (!wall || currentStage === "walls") return out;
  const info = getWallPlacementInfo(wall, walls);
  const wallLen = info ? info.len : 0;
  ((state && state.placements) || []).forEach((p) => {
    const def = effDef(p, lib);
    if (!def) return;
    if (!p.wallId) return; // legacy clrLeft path — WI-13 fallback
    if (p.wallId !== wall.id) return; // other wall — correctly absent
    const t = (p.absLeft != null ? p.absLeft : 0) + def.width / 2;
    if (t >= -0.05 && t <= wallLen + 0.05) {
      out.push({ def, axisPos: t, cabY: elevCabY(p, def), placementId: p.id });
    }
  });
  return out;
}

/** Global drawing ordinal (#N) — placements with a known def, in array order (~35749). */
export function elevOrdinalMap(placements, lib) {
  const map = new Map();
  let n = 0;
  (placements || []).forEach((p) => { if (lib[p.type]) { n += 1; map.set(p.id, n); } });
  return map;
}

/* ── CV detail parsers (~34112–34131) ─────────────────────────────────────── */

export function svgDoorCount(code) {
  if (!code) return 1;
  const c = code.toUpperCase();
  if (c.indexOf("DR-") === 0 || /DR\d/.test(c)) return 0;
  const m = c.match(/D(\d)/);
  return m ? parseInt(m[1], 10) : 1;
}
export function svgDrawerCount(code) {
  if (!code) return 0;
  const c = code.toUpperCase();
  const m = c.match(/DR(\d)/);
  return m ? parseInt(m[1], 10) : 0;
}
export function svgHingeSide(code) {
  if (!code) return "L";
  const c = code.toUpperCase();
  if (c.slice(-2) === "-R") return "R";
  if (c.slice(-2) === "-L") return "L";
  return "N";
}

/* ── EXPLICIT technician geometry (RC polish 2026-07-26) ─────────────────────
 * The Cabinet Technician can now declare doors/drawers/hinge on the product
 * (u.geometry → def.geometry via the catalog bridge). Explicit configuration
 * WINS; the SKU D-pattern convention remains the compatibility fallback for
 * every existing product. Dealers never need to learn SKU codes. */
export function defDoorCount(def, code) {
  const g = def && def.geometry;
  if (g && g.doors != null && g.doors !== "") {
    const n = parseInt(g.doors, 10);
    if (Number.isFinite(n)) return Math.max(0, Math.min(6, n));
  }
  return svgDoorCount(code);
}
export function defDrawerCount(def, code) {
  const g = def && def.geometry;
  if (g && g.drawers != null && g.drawers !== "") {
    const n = parseInt(g.drawers, 10);
    if (Number.isFinite(n)) return Math.max(0, Math.min(8, n));
  }
  return svgDrawerCount(code);
}
export function defHingeSide(def, code) {
  const g = def && def.geometry;
  if (g && (g.hinge === "L" || g.hinge === "R")) return g.hinge;
  return svgHingeSide(code);
}
/** ONE hinge rule for a placed instance (2026-08-20). Explicit Opening pick
 *  wins; then AUTOMATIC follows the gola (user mandate: "where i put automatic
 *  the door should open on the side of the gola" — a vertical channel on ONE
 *  side is the grip, so the door's FREE edge lands on it and the hinge goes to
 *  the opposite side); double gola / no gola fall back to the technician /
 *  SKU default. Consumed by orbit 3D, the module editor AND Present so every
 *  surface swings the same way. */
export function configHingeSide(def, code, config, notchSides) {
  const c = config || {};
  if (c.hingeDir === "Right") return "R";
  if (c.hingeDir === "Left") return "L";
  // A NEIGHBOUR's vertical gola standing at one side (2026-08-23 mandate:
  // "the moment this cabinet snaps to 30mm close to the cabinet with gola …
  // the door should open the side of the gola"): the profile beside the
  // cabinet IS the grip — the door's free edge lands on it, hinge opposite.
  if (notchSides && notchSides.L !== notchSides.R) {
    return notchSides.L ? "R" : "L";
  }
  if (c.gola && c.gola !== "None") {
    if (c.golaPos === "L") return "R";
    if (c.golaPos === "R") return "L";
  }
  return defHingeSide(def, code);
}
/** true when the instance's hinge is EXPLICIT (Opening pick, single-side
 *  gola, or a neighbour's gola notching ONE side) — an explicit side must
 *  also beat a front-layout row's authored hinge, which is only a default. */
export function configHingeExplicit(config, notchSides) {
  const c = config || {};
  if (c.hingeDir === "Right" || c.hingeDir === "Left") return true;
  if (notchSides && notchSides.L !== notchSides.R) return true;
  return !!(c.gola && c.gola !== "None" && (c.golaPos === "L" || c.golaPos === "R"));
}

/* ── Dimension chains (v0.41/v0.44/v0.45 pro dims) ────────────────────────── */

/** One-decimal mm label when fractional, integer otherwise (CV's "483.1", ~35786). */
export function dimMM(m) {
  // batch 9 (2026-09-28 user: "no need to put the number with a dot like 215.2 — round it:
  // 215.7 → 216"): whole millimetres, always
  return String(Math.round(m * 1000));
}

/** High units run on the TOP chain: wall / stack / bridge (~35819). */
export function roleHigh(r) {
  return r === "cabinet-wall" || r === "cabinet-stack" || r === "cabinet-bridge";
}

/** Drop consecutive duplicates within eps from a sorted list (~35853 filter). */
export function uniqSorted(values, eps = 0.005) {
  return [...values].sort((a, b) => a - b).filter((v, i, a) => i === 0 || Math.abs(v - a[i - 1]) > eps);
}

/** Cabinet width chain breakpoints: inner corners + every unit's left/right (~35822). */
export function cabChainPts(list, esp, wallLen) {
  const pts = [esp.off0];
  [...list].sort((a, b) => a.axisPos - b.axisPos).forEach((c) => {
    pts.push(c.axisPos - c.def.width / 2);
    pts.push(c.axisPos + c.def.width / 2);
  });
  pts.push(wallLen - esp.off1);
  // Merge only near-coincident breakpoints (< 0.5mm — touching units / FP noise).
  // The old 5mm threshold hid real small gaps between adjacent modules; a 1mm gap
  // must survive so it gets its own elevation dimension.
  return pts.filter((v, i) => i === 0 || Math.abs(v - pts[i - 1]) > 0.0005);
}

/** Opening width chain breakpoints (~35848–35853). */
export function openingWidthChainPts(opsOnWall, wallLen) {
  const pts = new Set([0, wallLen]);
  opsOnWall.forEach((op) => {
    pts.add(Math.max(0, Math.min(wallLen, op.u)));
    pts.add(Math.max(0, Math.min(wallLen, op.u + op.width)));
  });
  return uniqSorted([...pts]);
}

/** Opening height chain breakpoints — sill + head (~35924–35930). Doors sit on the floor;
 *  window sill defaults to 0.9. */
export function openingHeightChainPts(opsOnWall, wallH) {
  const pts = new Set([0, wallH]);
  opsOnWall.forEach((op) => {
    const h1 = op.type === "door" ? 0 : (op.sill != null ? op.sill : 0.9);
    pts.add(Math.max(0, Math.min(wallH, h1)));
    pts.add(Math.max(0, Math.min(wallH, h1 + op.height)));
  });
  return uniqSorted([...pts]);
}

/** Top-edge cut chain (~35868–35875): cut endpoints whose h is at the wall top. */
export function topCutChainPts(wall, wallLen, wallH) {
  const pts = new Set([0, wallLen]);
  let has = false;
  for (const c of (wall.elevCuts || [])) {
    if (Math.abs(c[1] - wallH) < ELEV_EDGE_TOL) { pts.add(c[0]); has = true; }
    if (Math.abs(c[3] - wallH) < ELEV_EDGE_TOL) { pts.add(c[2]); has = true; }
  }
  return { pts: uniqSorted([...pts]), has };
}

/** Right-edge cut chain (~35898–35904). */
export function rightCutChainPts(wall, wallLen, wallH) {
  const pts = new Set([0, wallH]);
  let has = false;
  for (const c of (wall.elevCuts || [])) {
    if (Math.abs(c[0] - wallLen) < ELEV_EDGE_TOL) { pts.add(c[1]); has = true; }
    if (Math.abs(c[2] - wallLen) < ELEV_EDGE_TOL) { pts.add(c[3]); has = true; }
  }
  return { pts: uniqSorted([...pts]), has };
}

/** Floor-feature chain: hole edges + floor-edge cuts (~36056–36069). */
export function floorFeaturePts(wall, wallLen) {
  const floorSet = new Set([0, wallLen]);
  let has = false;
  for (const ho of (wall.elevHoles || [])) {
    floorSet.add(Math.min(ho[0], ho[2])); floorSet.add(Math.max(ho[0], ho[2]));
    has = true;
  }
  for (const c of (wall.elevCuts || [])) {
    if (Math.abs(c[1]) < ELEV_EDGE_TOL) { floorSet.add(c[0]); has = true; }
    if (Math.abs(c[3]) < ELEV_EDGE_TOL) { floorSet.add(c[2]); has = true; }
  }
  return { pts: uniqSorted([...floorSet]), has };
}

/** LEFT unit-heights chain: floor, ceiling + every unit's bottom/top (~36030–36035). */
export function unitHeightsChainPts(onWall, wallH) {
  const vset = new Set([0, wallH]);
  onWall.forEach((c) => {
    vset.add(Math.max(0, Math.min(wallH, +(c.cabY - c.def.height / 2).toFixed(4))));
    vset.add(Math.max(0, Math.min(wallH, +(c.cabY + c.def.height / 2).toFixed(4))));
  });
  return uniqSorted([...vset]);
}

/* ── Openings on the elevation face (~50597–50610) ────────────────────────── */

/** Wall-local vertical box of an opening: doors floor-up, windows sill-up. */
export function openingElevBox(op) {
  const h1 = op.type === "door" ? 0 : (op.sill != null ? op.sill : 0.9);
  return { u: op.u, width: op.width, h1, h2: h1 + op.height };
}

/* ── v0.42 elevation DROP + drag-along-wall ───────────────────────────────── */

/** The v0.42 drop clamp (~46589–46594): [off0, len−off1−width], centering on the inner
 *  span when the cabinet is wider than it (matches snapToNearestWall's B5). u = wall-local
 *  drop x (metres); returns absLeft. */
export function elevDropAbsLeft(u, width, wall, walls) {
  const info = getWallPlacementInfo(wall, walls);
  if (!info) return null;
  const spE = wallInnerSpan(wall, walls);
  let _lo = spE.off0, _hi = info.len - spE.off1 - width;
  if (_hi < _lo) { const _c0 = spE.off0 + (spE.innerLen - width) / 2; _lo = _c0; _hi = _c0; }
  return Math.max(_lo, Math.min(u - width / 2, _hi));
}

/** v0.42 (LH): ADD CABINETS IN ELEVATION VIEW (~46577–46610). Places `type` on the viewed
 *  wall at dropped u, clamped to the inner span; partition/peninsula default faceSign=1;
 *  the WI-1 placementPipeline `ruleAllowPlacement` is the gate (engine ~46597 — NOTE the
 *  live elevation drop does NOT run resolveWallOverlap/maybeAutoCorner; do not add them),
 *  then push + magnet + `ruleAdvise` + the "Placed:" toast string (~46600–46607). Mutates
 *  state. Returns { ok, placement, warning, advice, toast } | { ok:false, reason }. */
export function elevationDropAt(state, type, wallId, u, lib) {
  const defDrop = lib[type];
  if (!defDrop) return { ok: false, reason: "Unknown module " + type };
  const wallE = state.walls.find((w) => w.id === wallId);
  if (!wallE) return { ok: false, reason: "Unknown wall " + wallId };
  const absLeft = elevDropAbsLeft(u, defDrop.width, wallE, state.walls);
  if (absLeft == null) return { ok: false, reason: "Wall has zero length" };
  const np = { id: newPlacementId(), type, wallId: wallE.id, absLeft, roomSensed: true, config: {} };
  if (wallE.style === "partition" || wallE.style === "peninsula") {
    /* INHERIT THE RUN'S FACE (2026-08-29 adversarial review): the elevation
     * flattens both faces into one view, so a drop "into" a face −1 run used
     * to land hardcoded on face +1 — overlapping the −1 modules invisibly and
     * (now) reading as back-to-back to the backsplash rule. The nearest
     * module on this wall says which face the user is actually looking at. */
    let nearest = null, nd = Infinity;
    (state.placements || []).forEach((q) => {
      if (!q || q.wallId !== wallE.id || q.absLeft == null) return;
      const dd = Math.abs(q.absLeft - absLeft);
      if (dd < nd) { nd = dd; nearest = q; }
    });
    np.faceSign = nearest && nearest.faceSign === -1 ? -1 : 1;
  }
  /* GHOST == DROP, gate order (2026-08-30 user: "why can't I place a cabinet
   * even though it's GREEN"): the elevation ghost validates the SNAPPED
   * position (magnet + exact-fit first, then the gate), but the drop gated
   * the RAW pointer position and only snapped afterwards — so a release whose
   * raw point nicked a neighbour was refused ("Won't fit — overlaps ...")
   * while the ghost, seated in the free slot by the magnet, showed green.
   * Snap FIRST, judge the seated position — the exact pair the ghost runs. */
  np.absLeft = snapSides(np, state.placements, lib, state.walls); // (~46600)
  // TIGHT-GAP AUTO-SEAT (2026-07-14, Issue 4): a near-exact opening seats itself.
  np.absLeft = snapExactFit(np, defDrop, np.absLeft, state.placements, lib, state.walls);
  // STACK LAW (batch 9): an occupied span lifts the unit above what it overlaps when it fits
  { const st = stackElevationFor(np, defDrop, state, lib); if (st) applyStack(np, st); }
  const allow = ruleAllowPlacement(np, state, lib); // (~46597) — hard gate, nothing placed on refusal
  if (!allow.ok) return { ok: false, reason: allow.reason, placement: np };
  state.placements.push(np);
  const advice = ruleAdvise(np, state, lib); // (~46606)
  return { ok: true, placement: np, warning: allow.warning || null, advice, toast: "Placed: " + defDrop.name }; // (~46607)
}

/** Live elevation drag move — the plan drag's same-wall snap branch expressed in wall-local
 *  u (~37972–37994): inner-span clamp (with B5 centering) then P6 clampToContact from the
 *  current position. Mutates p.absLeft (NO validate, NO magnet — that's the commit).
 *  Returns the applied absLeft (or null when the placement/wall is missing). */
export function elevationMoveTo(state, id, desiredAbsLeft, lib) {
  const p = state.placements.find((x) => x.id === id);
  if (!p) return null;
  const def = lib[p.type];
  const wall = state.walls.find((w) => w.id === p.wallId);
  if (!def || !wall) return null;
  const clamped = elevDropAbsLeft(desiredAbsLeft + def.width / 2, def.width, wall, state.walls);
  if (clamped == null) return null;
  const fromLeft = typeof p.absLeft === "number" ? p.absLeft : null;
  // TIGHT-GAP AUTO-SEAT (batch 7, 2026-09-28): the elevation drag had no seat at all —
  // an opening that fits the unit exactly was unreachable (the contact clamp pushed the
  // drag back out). Same rule as the plan drag: checked BEFORE the clamp.
  const fit = snapExactFitEx(p, def, clamped, state.placements, lib, state.walls);
  p.absLeft = fit.seated ? fit.absLeft : clampToContact(p, def, clamped, fromLeft, state.placements, lib, state.walls);
  return p.absLeft;
}

/** Commit an elevation drag: validate → revert to orig on failure → v0.32 B4 directional
 *  magnet (~38014–38029). orig = { wallId, absLeft } captured at pickup. Mutates p. */
export function elevationCommitMove(state, id, orig, lib, moveDir) {
  const p = state.placements.find((x) => x.id === id);
  if (!p) return { ok: false, reason: "No placement " + id };
  p.roomSensed = true; // v0.31a: moving = re-placing (~38028)
  return commitMove(p, state.walls, state.placements, lib, state.kitchenWizard, state.wallOpenings, orig, moveDir);
}

/* ── Edit-Position Elevation tab hooks (LOCK-EPE-01/02) ───────────────────── */

/** Role mounting base in mm (~51883). def should be the EFFECTIVE def (custom height). */
export function roleBaseMm(def) {
  return Math.round(roleBaseY(def) * 1000);
}

/** v0.46 (LH ③): the tab shows the unit's REAL height off the floor = role base + stored
 *  delta (~51883–51885, ~51897–51901). Returns mm: { roleBaseMm, elevationMm (= Bottom),
 *  topMm, bottomMm, heightMm } for the Height Off Floor section. */
export function elevationFieldValues(p, def) {
  const cfg = p.config || {};
  const pos = cfg.position || {};
  const _rbMm = roleBaseMm(def);
  const heightMm = Math.round((def.height || 0) * 1000);
  const elevMm = _rbMm + Math.round(pos.elevation || 0);
  return { roleBaseMm: _rbMm, elevationMm: elevMm, topMm: elevMm + heightMm, bottomMm: elevMm, heightMm };
}

/** LOCK-EPE-02 write path (~52214–52220): the field is the ABSOLUTE height off floor (mm);
 *  we store the DELTA from the role base in cfg.position.elevation so the geometry pipeline
 *  (additive override, read /1000) is untouched. Mutates p. Returns the stored delta (mm). */
export function setElevationOffFloorMm(p, def, absMm) {
  if (!p.config) p.config = {};
  if (!p.config.position) p.config.position = { angle: 0, outset: 0, elevation: 0 };
  const delta = (+absMm || 0) - roleBaseMm(def);
  p.config.position.elevation = delta;
  return delta;
}

/* ═════════════════════════ WI-7: ELEVATION RIBBON TOOLS ═════════════════════════
 * Measure / typed Guide / Clear-wall-guides / Cut / Opening / Circle, ported
 * VERBATIM from lhc-app.html per LOCKED TOOLBAR SPEC v1 (~18974–19081):
 *   wallDrawTool elevation fields (~29510–29564)
 *   setWallTool per-tool state reset (~36371–36397)
 *   snapElevPoint two-pass feature snap (~37145–37209)
 *   elevation click branch — guide/cut/opening/circle/measure (~36582–36736)
 *   elevation mousemove previews + snapMark (~37297–37354)
 *   guide ruler mm typing (~37674–37733) · circle radius mm typing (~37736–37775)
 *   rb-clear-guides — wipe active wall's guides, keep cuts (~36438–36457)
 *
 * Committed geometry lives ON the wall object, in the iframe's EXACT shapes so walls
 * serialize identically through buildNativeSnapshot (getState deep-copies walls, 48506):
 *   wall.guides      [{ axis:'v'|'h', pos, ts }]  (legacy [u,h] crosses still render)
 *   wall.elevCuts    [[u1, h1, u2, h2, ts]]       (floor-side of the line is kept)
 *   wall.elevHoles   [[u1, h1, u2, h2, ts]]       (rectangular punch)
 *   wall.elevCircles [[cu, ch, r, ts]]            (round punch)
 * ts = Date.now() + array.length — the iframe's collision-proof stamp, reproduced.
 *
 * UNDO PARITY NOTE: the iframe does NOT push pushUndo/pushWallHistory for these commits
 * (its ribbon Undo/Redo was removed by LOCKED TOOLBAR SPEC v1; the vestigial
 * wallDrawTool.redoStack pop-last mechanism has no remaining UI). We reproduce that:
 * no history hooks here — only the symbol/opening DELETE paths snapshot history
 * (LOCK-SL-08). Tool state is mutated in place, engine-style.
 */

/** The wallDrawTool subset the elevation tools use (~29510–29564). */
export function createElevToolState() {
  return {
    measureA: null, measureB: null, measurePreview: null,
    cutA: null, cutPreview: null,
    guidePreview: null, guideAnchor: null, guideAxis: null, guideEnd: null, guideTyping: "",
    snapMark: null,
    openingA: null, openingPreview: null,
    circleA: null, circlePreview: null, circleTyping: "",
    redoStack: [], // vestigial (~29561) — kept so commits can clear it like the engine
  };
}

/** setWallTool's tool-state reset (~36371–36397): every switch cancels all pending ops. */
export function resetElevToolState(t) {
  t.measureA = null; t.measureB = null; t.measurePreview = null;
  t.cutA = null; t.cutPreview = null;
  t.guidePreview = null; t.guideAnchor = null; t.guideAxis = null; t.guideEnd = null; t.guideTyping = "";
  t.snapMark = null;
  t.openingA = null; t.openingPreview = null;
  t.circleA = null; t.circlePreview = null; t.circleTyping = "";
  return t;
}

/** snapElevPoint (~37145–37209) — two-pass feature snap on the active wall face.
 *  Pass 1 POINT snaps (corners/midpoints/center/cut vertices/circle centres/guide
 *  intersections, closest within tol wins, both axes snap). Pass 2 LINE snaps (edges/
 *  midlines/guides/cut projections, u and h independent). tol default 0.15 m.
 *  guidesHidden replaces the engine's global wallDrawTool.guidesHidden read.
 *  Returns [u, h, snapInfo|null] — snapInfo { type:'point',u,h } | { type:'line',u,h,isV,isH }. */
export function snapElevPoint(u, h, wall, wallLen, wallH, tol, guidesHidden) {
  tol = tol || 0.15;
  const tol2 = tol * tol;

  // ── Pass 1: POINT snaps ────────────────────────────────────────────
  const pts = [
    [0, 0], [wallLen, 0], [wallLen, wallH], [0, wallH],
    [wallLen / 2, 0], [wallLen, wallH / 2], [wallLen / 2, wallH], [0, wallH / 2],
    [wallLen / 2, wallH / 2],
  ];
  if (wall.elevCuts) {
    for (const c of wall.elevCuts) pts.push([c[0], c[1]], [c[2], c[3]]);
  }
  if (wall.elevCircles) {
    for (const c of wall.elevCircles) pts.push([c[0], c[1]]);
  }
  if (wall.guides && !guidesHidden) {
    const vGs = [], hGs = [];
    for (const g of wall.guides) {
      if (Array.isArray(g)) pts.push([g[0], g[1]]); // legacy cross
      else if (g && g.axis === "v") vGs.push(g.pos);
      else if (g && g.axis === "h") hGs.push(g.pos);
    }
    for (const v of vGs) for (const hh of hGs) pts.push([v, hh]);
  }
  let best = null, bestD2 = tol2;
  for (const p of pts) {
    const d2 = (u - p[0]) * (u - p[0]) + (h - p[1]) * (h - p[1]);
    if (d2 < bestD2) { bestD2 = d2; best = p; }
  }
  if (best) return [best[0], best[1], { type: "point", u: best[0], h: best[1] }];

  // ── Pass 2: LINE snaps (independent in u and h) ────────────────────
  let su = u, sh = h, snapU = false, snapH = false;
  if (Math.abs(u) < tol) { su = 0; snapU = true; }
  else if (Math.abs(u - wallLen) < tol) { su = wallLen; snapU = true; }
  else if (Math.abs(u - wallLen / 2) < tol) { su = wallLen / 2; snapU = true; }
  if (Math.abs(h) < tol) { sh = 0; snapH = true; }
  else if (Math.abs(h - wallH) < tol) { sh = wallH; snapH = true; }
  else if (Math.abs(h - wallH / 2) < tol) { sh = wallH / 2; snapH = true; }
  if (wall.guides && !guidesHidden) {
    for (const g of wall.guides) {
      if (Array.isArray(g)) {
        if (Math.abs(u - g[0]) < tol) { su = g[0]; snapU = true; }
        if (Math.abs(h - g[1]) < tol) { sh = g[1]; snapH = true; }
      } else if (g && g.axis === "v" && Math.abs(u - g.pos) < tol) { su = g.pos; snapU = true; }
      else if (g && g.axis === "h" && Math.abs(h - g.pos) < tol) { sh = g.pos; snapH = true; }
    }
  }
  if (wall.elevCuts) {
    for (const c of wall.elevCuts) {
      if (Math.abs(u - c[0]) < tol) { su = c[0]; snapU = true; }
      if (Math.abs(u - c[2]) < tol) { su = c[2]; snapU = true; }
      if (Math.abs(h - c[1]) < tol) { sh = c[1]; snapH = true; }
      if (Math.abs(h - c[3]) < tol) { sh = c[3]; snapH = true; }
    }
  }
  if (snapU || snapH) return [su, sh, { type: "line", u: su, h: sh, isV: snapU, isH: snapH }];
  return [su, sh, null];
}

const _clampVal = (v, min, max) => Math.max(min, Math.min(max, v));

/** Guide tool click (~36599–36643): 1st click anchors (snapped); 2nd click (axis locked
 *  by movement) drops a perpendicular guide at the snapped pos on a 5 cm grid.
 *  Returns { render:true, toast?, committed?:'guide' }. */
export function elevGuideClick(t, wall, u, h, wallLen, wallH, guidesHidden) {
  const cu = _clampVal(u, 0, wallLen), ch = _clampVal(h, 0, wallH);
  if (!t.guideAnchor) {
    const [snU, snH] = snapElevPoint(cu, ch, wall, wallLen, wallH, undefined, guidesHidden);
    t.guideAnchor = [snU, snH];
    t.guideAxis = null;
    t.guideEnd = [snU, snH];
    t.guideTyping = "";
    return { render: true, toast: "Anchored — move the cursor to set the direction, then click or type a length + Enter" };
  }
  const axis = t.guideAxis;
  if (!axis) return { render: true, toast: "Move the cursor first to choose horizontal or vertical" };
  const [snU, snH] = snapElevPoint(cu, ch, wall, wallLen, wallH, undefined, guidesHidden);
  if (!wall.guides) wall.guides = [];
  let toast;
  if (axis === "h") { // horizontal ruler → VERTICAL guide at u = snU
    const pos = Math.round(snU / 0.05) * 0.05;
    const dist = Math.abs(pos - t.guideAnchor[0]);
    wall.guides.push({ axis: "v", pos, ts: Date.now() + wall.guides.length });
    toast = `Vertical guide at ${Math.round(dist * 1000)} mm from anchor`;
  } else { // vertical ruler → HORIZONTAL guide at h = snH
    const pos = Math.round(snH / 0.05) * 0.05;
    const dist = Math.abs(pos - t.guideAnchor[1]);
    wall.guides.push({ axis: "h", pos, ts: Date.now() + wall.guides.length });
    toast = `Horizontal guide at ${Math.round(dist * 1000)} mm from anchor`;
  }
  t.guideAnchor = null; t.guideAxis = null; t.guideEnd = null; t.guideTyping = "";
  t.redoStack = [];
  return { render: true, toast, committed: "guide" };
}

/** Cut tool click (~36646–36667): two snapped points → wall.elevCuts entry (floor side
 *  kept). committed:'cut' requires a 3D rebuild (engine calls buildWalls). */
export function elevCutClick(t, wall, u, h, wallLen, wallH, guidesHidden) {
  const cu = _clampVal(u, 0, wallLen), ch = _clampVal(h, 0, wallH);
  const [snU, snH] = snapElevPoint(cu, ch, wall, wallLen, wallH, undefined, guidesHidden);
  if (!t.cutA) {
    t.cutA = [snU, snH];
    return { render: true };
  }
  const [au, ah] = t.cutA;
  const lineLen = Math.hypot(snU - au, snH - ah);
  let out;
  if (lineLen < 0.05) {
    out = { render: true, toast: "Cut line too short" };
  } else {
    if (!wall.elevCuts) wall.elevCuts = [];
    wall.elevCuts.push([au, ah, snU, snH, Date.now() + wall.elevCuts.length]);
    t.redoStack = [];
    out = { render: true, toast: "Wall cut applied", committed: "cut" };
  }
  t.cutA = null; t.cutPreview = null;
  return out;
}

/** Opening tool click (~36668–36692): 2-click snapped rectangle → wall.elevHoles. */
export function elevOpeningClick(t, wall, u, h, wallLen, wallH, guidesHidden) {
  const cu = _clampVal(u, 0, wallLen), ch = _clampVal(h, 0, wallH);
  const [snU, snH] = snapElevPoint(cu, ch, wall, wallLen, wallH, undefined, guidesHidden);
  if (!t.openingA) {
    t.openingA = [snU, snH];
    return { render: true, toast: "Click the opposite corner" };
  }
  const [au, ah] = t.openingA;
  const w = Math.abs(snU - au), hgt = Math.abs(snH - ah);
  let out;
  if (w < 0.05 || hgt < 0.05) {
    out = { render: true, toast: "Opening too small — needs both width and height" };
  } else {
    if (!wall.elevHoles) wall.elevHoles = [];
    wall.elevHoles.push([au, ah, snU, snH, Date.now() + wall.elevHoles.length]);
    t.redoStack = [];
    out = { render: true, toast: `Opening cut: ${Math.round(w * 1000)} × ${Math.round(hgt * 1000)} mm`, committed: "opening" };
  }
  t.openingA = null; t.openingPreview = null;
  return out;
}

/** Circle tool click (~36693–36722): centre then radius click (typed mm overrides). */
export function elevCircleClick(t, wall, u, h, wallLen, wallH, guidesHidden) {
  const cu = _clampVal(u, 0, wallLen), ch = _clampVal(h, 0, wallH);
  const [snU, snH] = snapElevPoint(cu, ch, wall, wallLen, wallH, undefined, guidesHidden);
  if (!t.circleA) {
    t.circleA = [snU, snH];
    t.circleTyping = "";
    return { render: true, toast: "Move out to set the radius — click, or type a radius + Enter" };
  }
  const [au, ah] = t.circleA;
  let r = Math.hypot(snU - au, snH - ah);
  if (t.circleTyping) {
    const v = parseFloat(t.circleTyping);
    if (!isNaN(v) && v > 0) r = v / 1000;
  }
  let out;
  if (r < 0.03) {
    out = { render: true, toast: "Circle too small" };
  } else {
    if (!wall.elevCircles) wall.elevCircles = [];
    wall.elevCircles.push([au, ah, r, Date.now() + wall.elevCircles.length]);
    t.redoStack = [];
    out = { render: true, toast: `Round opening: ⌀ ${Math.round(r * 2000)} mm`, committed: "circle" };
  }
  t.circleA = null; t.circlePreview = null; t.circleTyping = "";
  return out;
}

/** Measure tool click (~36723–36734): clamped (UNSNAPPED) points; 3rd click restarts. */
export function elevMeasureClick(t, u, h, wallLen, wallH) {
  const cu = _clampVal(u, 0, wallLen), ch = _clampVal(h, 0, wallH);
  if (!t.measureA || t.measureB) {
    t.measureA = [cu, ch];
    t.measureB = null;
    t.measurePreview = null;
  } else {
    t.measureB = [cu, ch];
    t.measurePreview = null;
  }
  return { render: true };
}

/** Mousemove previews (~37297–37354): guide ruler + axis lock (>5 cm), cut/opening/circle
 *  snapped previews + snapMark, measure raw preview. Returns true when a re-render is due. */
export function elevToolMove(t, mode, wall, u, h, wallLen, wallH, guidesHidden) {
  if (mode === "guide") {
    const sn = snapElevPoint(u, h, wall, wallLen, wallH, undefined, guidesHidden);
    const snapped = [sn[0], sn[1]];
    t.snapMark = sn[2] || null;
    if (t.guideAnchor) {
      t.guideEnd = snapped;
      if (!t.guideAxis) {
        const dU = Math.abs(snapped[0] - t.guideAnchor[0]);
        const dH = Math.abs(snapped[1] - t.guideAnchor[1]);
        if (Math.max(dU, dH) > 0.05) t.guideAxis = (dU > dH) ? "h" : "v";
      }
    } else {
      t.guidePreview = snapped;
    }
    return true;
  }
  if (mode === "cut") {
    const sn = snapElevPoint(u, h, wall, wallLen, wallH, undefined, guidesHidden);
    t.snapMark = sn[2] || null;
    if (t.cutA) t.cutPreview = [sn[0], sn[1]];
    return true;
  }
  if (mode === "opening") {
    const sn = snapElevPoint(u, h, wall, wallLen, wallH, undefined, guidesHidden);
    t.snapMark = sn[2] || null;
    if (t.openingA) t.openingPreview = [sn[0], sn[1]];
    return true;
  }
  if (mode === "circle") {
    const sn = snapElevPoint(u, h, wall, wallLen, wallH, undefined, guidesHidden);
    t.snapMark = sn[2] || null;
    if (t.circleA) t.circlePreview = [sn[0], sn[1]];
    return true;
  }
  if (mode === "measure" && t.measureA && !t.measureB) {
    t.measurePreview = [u, h];
    return true;
  }
  return false;
}

/** Guide ruler typing (~37674–37733): digits accumulate; Backspace edits; Enter drops the
 *  guide at the typed mm along the locked axis (direction from the cursor side, clamped to
 *  the wall); Escape cancels the anchor. Caller gates on t.guideAnchor being set.
 *  Returns { handled, render, toast?, committed?:'guide' }. */
export function elevGuideKey(t, wall, key, wallLen, wallH) {
  if (/^[0-9]$/.test(key)) {
    t.guideTyping = (t.guideTyping || "") + key;
    return { handled: true, render: true };
  }
  if (key === "Backspace") {
    t.guideTyping = (t.guideTyping || "").slice(0, -1);
    return { handled: true, render: true };
  }
  if (key === "Enter" && t.guideTyping && t.guideAxis) {
    const distMm = parseInt(t.guideTyping, 10);
    let toast, committed;
    if (isFinite(distMm) && distMm > 0 && wall) {
      const [au, ah] = t.guideAnchor;
      const cur = t.guideEnd || t.guideAnchor;
      const axis = t.guideAxis;
      const distM = distMm / 1000;
      let pos;
      if (axis === "h") {
        const dir = (cur[0] >= au) ? 1 : -1;
        pos = _clampVal(au + dir * distM, 0, wallLen);
        if (!wall.guides) wall.guides = [];
        wall.guides.push({ axis: "v", pos, ts: Date.now() + wall.guides.length });
      } else {
        const dir = (cur[1] >= ah) ? 1 : -1;
        pos = _clampVal(ah + dir * distM, 0, wallH);
        if (!wall.guides) wall.guides = [];
        wall.guides.push({ axis: "h", pos, ts: Date.now() + wall.guides.length });
      }
      toast = `Guide at ${distMm} mm from anchor`;
      committed = "guide";
      t.redoStack = [];
    }
    t.guideAnchor = null; t.guideAxis = null; t.guideEnd = null; t.guideTyping = "";
    return { handled: true, render: true, toast, committed };
  }
  if (key === "Escape") {
    t.guideAnchor = null; t.guideAxis = null; t.guideEnd = null; t.guideTyping = "";
    return { handled: true, render: true };
  }
  return { handled: false, render: false };
}

/** Circle radius typing (~37736–37775): digits + one '.', Backspace, Enter commits (typed
 *  mm overrides the preview radius; <0.03 m refuses but still resets), Escape cancels.
 *  Caller gates on t.circleA being set. Returns { handled, render, toast?, committed? }. */
export function elevCircleKey(t, wall, key) {
  if (/^[0-9]$/.test(key) || (key === "." && !t.circleTyping.includes("."))) {
    t.circleTyping += key;
    return { handled: true, render: true };
  }
  if (key === "Backspace") {
    t.circleTyping = t.circleTyping.slice(0, -1);
    return { handled: true, render: true };
  }
  if (key === "Enter") {
    let toast, committed;
    if (wall) {
      const [au, ah] = t.circleA;
      let r = t.circlePreview ? Math.hypot(t.circlePreview[0] - au, t.circlePreview[1] - ah) : 0;
      const v = parseFloat(t.circleTyping);
      if (!isNaN(v) && v > 0) r = v / 1000;
      if (r >= 0.03) {
        if (!wall.elevCircles) wall.elevCircles = [];
        wall.elevCircles.push([au, ah, r, Date.now() + wall.elevCircles.length]);
        t.redoStack = [];
        toast = `Round opening: ⌀ ${Math.round(r * 2000)} mm`;
        committed = "circle";
      }
    }
    t.circleA = null; t.circlePreview = null; t.circleTyping = "";
    return { handled: true, render: true, toast, committed };
  }
  if (key === "Escape") {
    t.circleA = null; t.circlePreview = null; t.circleTyping = "";
    return { handled: true, render: true };
  }
  return { handled: false, render: false };
}

/** rb-clear-guides (~36438–36457): wipe EVERY guide on the wall, keep cuts; resets the
 *  in-progress ruler + redo stack. Returns { cleared, toast }. */
export function clearWallGuides(t, wall) {
  if (!wall || !wall.guides || !wall.guides.length) {
    return { cleared: 0, toast: "No guides to clear on this wall" };
  }
  const n = wall.guides.length;
  wall.guides = [];
  if (t) {
    t.redoStack = [];
    t.guideAnchor = null; t.guideAxis = null; t.guideEnd = null; t.guideTyping = "";
  }
  return { cleared: n, toast: `Cleared ${n} guide${n === 1 ? "" : "s"}` };
}

/* ═════════════════════════ WI-7: SYMBOLS (walls + elevation) ═════════════════════════
 * SYMBOL_LIBRARY / SYMBOL_SVGS / getSymbolDef ported VERBATIM (~49891–50008). All three
 * live in ONE module per LOCK-SL-03 (library, SVGs and defs must stay in sync).
 * Placements are state.symbolPlacements [{ id, wallId, key, u, h, label }] — the exact
 * getState() field insertion order (~50307), so JSON serialization is byte-identical. */

export const SYMBOL_LIBRARY = {
  Electrical: {
    "switch":      { label: "Switch",     code: "SW",  defaultH: 1.10, desc: "Wall light switch." },
    "switch-dim":  { label: "Dimmer",     code: "DIM", defaultH: 1.10, desc: "Dimmable light switch." },
    "switch-2":    { label: "2-Way Sw",   code: "SW2", defaultH: 1.10, desc: "Two-way switch — control one light from two points." },
    "outlet":      { label: "Outlet",     code: "PO",  defaultH: 0.30, desc: "Single power socket." },
    "outlet-2x":   { label: "Outlet 2x",  code: "PO2", defaultH: 0.30, desc: "Double power socket." },
    "outlet-4x":   { label: "Outlet 4x",  code: "PO4", defaultH: 0.30, desc: "Quad socket bank." },
    "usb":         { label: "USB",        code: "USB", defaultH: 0.30, desc: "USB charging socket." },
    "data":        { label: "Data",       code: "DAT", defaultH: 0.30, desc: "Network / data point (RJ45)." },
    "tv":          { label: "TV / Cable", code: "TV",  defaultH: 0.30, desc: "TV / coaxial cable point." },
    "junction":    { label: "Junction",   code: "JB",  defaultH: 1.20, desc: "Electrical junction box." },
    "led-driver":  { label: "LED Drv",    code: "LED", defaultH: 2.20, desc: "LED strip driver / transformer." },
  },
  Plumbing: {
    "water-hot":   { label: "Hot Water",  code: "HW",  defaultH: 0.60, desc: "Hot water supply point." },
    "water-cold":  { label: "Cold Water", code: "CW",  defaultH: 0.60, desc: "Cold water supply point." },
    "drain":       { label: "Drain",      code: "DR",  defaultH: 0.30, desc: "Waste / drain outlet." },
    "gas":         { label: "Gas",        code: "GAS", defaultH: 0.30, desc: "Gas supply connection." },
    "dw-pair":     { label: "Dishwasher", code: "DW",  defaultH: 0.45, desc: "Dishwasher supply + drain pair." },
    "wm-pair":     { label: "Washer",     code: "WM",  defaultH: 0.80, desc: "Washing machine supply + drain pair." },
  },
  Ventilation: {
    "vent-hub":    { label: "Vent Hub",   code: "VH",  defaultH: 2.10, desc: "Ventilation connection hub." },
    "hood-duct":   { label: "Hood Duct",  code: "HD",  defaultH: 2.30, desc: "Extractor hood duct outlet." },
    "hvac-supply": { label: "HVAC Sup",   code: "HS",  defaultH: 2.20, desc: "HVAC supply air vent." },
    "hvac-return": { label: "HVAC Ret",   code: "HR",  defaultH: 2.20, desc: "HVAC return air vent." },
  },
  Structural: {
    "beam":        { label: "Beam Edge",  code: "BM",  defaultH: 2.40, desc: "Structural beam edge marker." },
    "niche":       { label: "Niche",      code: "NC",  defaultH: 1.20, desc: "Recessed wall niche." },
  },
};

/* AutoCAD-style symbol linework (~49891–49961): 0-24 viewBox centred on (12,12); the same
 * SVG is the panel icon AND the placed elevation glyph (scaled). */
export const SYMBOL_SVGS = {
  // ── Electrical (per NCS conventions: switches in circles, outlets with slot lines) ──
  "switch":      `<circle cx="12" cy="12" r="9" fill="white" stroke="currentColor" stroke-width="1.2"/>
                  <text x="12" y="15.5" text-anchor="middle" font-size="11" font-weight="600" fill="currentColor" stroke="none" style="font-family:var(--font)">S</text>`,
  "switch-dim":  `<circle cx="12" cy="12" r="9" fill="white" stroke="currentColor" stroke-width="1.2"/>
                  <text x="11" y="14" text-anchor="middle" font-size="9" font-weight="600" fill="currentColor" stroke="none" style="font-family:var(--font)">S</text>
                  <text x="14" y="18.5" text-anchor="middle" font-size="6" font-weight="600" fill="currentColor" stroke="none" style="font-family:var(--font)">D</text>`,
  "switch-2":    `<circle cx="12" cy="12" r="9" fill="white" stroke="currentColor" stroke-width="1.2"/>
                  <text x="11" y="14" text-anchor="middle" font-size="9" font-weight="600" fill="currentColor" stroke="none" style="font-family:var(--font)">S</text>
                  <text x="14" y="18.5" text-anchor="middle" font-size="6" font-weight="600" fill="currentColor" stroke="none" style="font-family:var(--font)">3</text>`,
  "outlet":      `<circle cx="12" cy="12" r="9" fill="white" stroke="currentColor" stroke-width="1.2"/>
                  <line x1="9.5" y1="9" x2="9.5" y2="15" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                  <line x1="14.5" y1="9" x2="14.5" y2="15" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>`,
  "outlet-2x":   `<circle cx="12" cy="12" r="9" fill="white" stroke="currentColor" stroke-width="1.2"/>
                  <line x1="7.5" y1="9" x2="7.5" y2="15" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
                  <line x1="10.5" y1="9" x2="10.5" y2="15" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
                  <line x1="13.5" y1="9" x2="13.5" y2="15" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
                  <line x1="16.5" y1="9" x2="16.5" y2="15" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>`,
  "outlet-4x":   `<rect x="3" y="7" width="18" height="10" fill="white" stroke="currentColor" stroke-width="1.2" rx="0.5"/>
                  <line x1="7" y1="9.5" x2="7" y2="14.5" stroke="currentColor" stroke-width="1.3"/>
                  <line x1="10" y1="9.5" x2="10" y2="14.5" stroke="currentColor" stroke-width="1.3"/>
                  <line x1="14" y1="9.5" x2="14" y2="14.5" stroke="currentColor" stroke-width="1.3"/>
                  <line x1="17" y1="9.5" x2="17" y2="14.5" stroke="currentColor" stroke-width="1.3"/>`,
  "usb":         `<circle cx="12" cy="12" r="9" fill="white" stroke="currentColor" stroke-width="1.2"/>
                  <text x="12" y="14.5" text-anchor="middle" font-size="5.5" font-weight="700" fill="currentColor" stroke="none" style="font-family:var(--font); letter-spacing: 0.3px;">USB</text>`,
  "data":        `<circle cx="12" cy="12" r="9" fill="white" stroke="currentColor" stroke-width="1.2"/>
                  <path d="M8 9 L16 9 L16 13 L12 16 L8 13 Z" fill="none" stroke="currentColor" stroke-width="1.2"/>`,
  "tv":          `<circle cx="12" cy="12" r="9" fill="white" stroke="currentColor" stroke-width="1.2"/>
                  <polygon points="8.5,10 15.5,10 12,16" fill="currentColor" stroke="none"/>`,
  "junction":    `<rect x="5" y="5" width="14" height="14" fill="white" stroke="currentColor" stroke-width="1.2"/>
                  <line x1="7.5" y1="7.5" x2="16.5" y2="16.5" stroke="currentColor" stroke-width="1.2"/>
                  <line x1="16.5" y1="7.5" x2="7.5" y2="16.5" stroke="currentColor" stroke-width="1.2"/>`,
  "led-driver":  `<polygon points="12,3.5 20.5,8 20.5,16 12,20.5 3.5,16 3.5,8" fill="white" stroke="currentColor" stroke-width="1.2"/>
                  <line x1="7" y1="8.5" x2="17" y2="15.5" stroke="currentColor" stroke-width="1.1"/>
                  <line x1="17" y1="8.5" x2="7" y2="15.5" stroke="currentColor" stroke-width="1.1"/>`,

  // ── Plumbing ──
  "water-hot":   `<circle cx="12" cy="12" r="9" fill="white" stroke="currentColor" stroke-width="1.2"/>
                  <text x="12" y="15" text-anchor="middle" font-size="9.5" font-weight="600" fill="currentColor" stroke="none" style="font-family:var(--font)">H</text>`,
  "water-cold":  `<circle cx="12" cy="12" r="9" fill="white" stroke="currentColor" stroke-width="1.2"/>
                  <text x="12" y="15" text-anchor="middle" font-size="9.5" font-weight="600" fill="currentColor" stroke="none" style="font-family:var(--font)">C</text>`,
  "drain":       `<circle cx="12" cy="12" r="9" fill="white" stroke="currentColor" stroke-width="1.2"/>
                  <line x1="7" y1="10" x2="14" y2="17" stroke="currentColor" stroke-width="0.9"/>
                  <line x1="10" y1="7" x2="17" y2="14" stroke="currentColor" stroke-width="0.9"/>
                  <line x1="7" y1="14" x2="10" y2="17" stroke="currentColor" stroke-width="0.9"/>
                  <line x1="14" y1="7" x2="17" y2="10" stroke="currentColor" stroke-width="0.9"/>`,
  "gas":         `<polygon points="12,3.5 20.5,8 20.5,16 12,20.5 3.5,16 3.5,8" fill="white" stroke="currentColor" stroke-width="1.2"/>
                  <text x="12" y="15" text-anchor="middle" font-size="9" font-weight="600" fill="currentColor" stroke="none" style="font-family:var(--font)">G</text>`,
  "dw-pair":     `<rect x="5" y="5" width="14" height="14" fill="white" stroke="currentColor" stroke-width="1.2" rx="0.5"/>
                  <text x="12" y="15" text-anchor="middle" font-size="7" font-weight="600" fill="currentColor" stroke="none" style="font-family:var(--font)">DW</text>`,
  "wm-pair":     `<rect x="5" y="5" width="14" height="14" fill="white" stroke="currentColor" stroke-width="1.2" rx="0.5"/>
                  <text x="12" y="15" text-anchor="middle" font-size="7" font-weight="600" fill="currentColor" stroke="none" style="font-family:var(--font)">WM</text>`,

  // ── Ventilation ──
  "vent-hub":    `<circle cx="12" cy="12" r="9" fill="white" stroke="currentColor" stroke-width="1.2"/>
                  <path d="M12 6 Q14.5 9, 12 12 Q9.5 15, 12 18" fill="none" stroke="currentColor" stroke-width="1.1"/>
                  <path d="M6 12 Q9 14.5, 12 12 Q15 9.5, 18 12" fill="none" stroke="currentColor" stroke-width="1.1"/>
                  <circle cx="12" cy="12" r="1.2" fill="currentColor"/>`,
  "hood-duct":   `<rect x="5" y="7" width="14" height="10" fill="white" stroke="currentColor" stroke-width="1.2"/>
                  <path d="M9 14 L12 9 L15 14" fill="none" stroke="currentColor" stroke-width="1.1"/>`,
  "hvac-supply": `<rect x="4" y="7" width="16" height="10" fill="white" stroke="currentColor" stroke-width="1.2"/>
                  <path d="M7 12 L16 12 M13 9.5 L16 12 L13 14.5" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>`,
  "hvac-return": `<rect x="4" y="7" width="16" height="10" fill="white" stroke="currentColor" stroke-width="1.2"/>
                  <path d="M17 12 L8 12 M11 9.5 L8 12 L11 14.5" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>`,

  // ── Structural ──
  "beam":        `<path d="M3 9.5 L21 9.5" stroke="currentColor" stroke-width="1.2" stroke-dasharray="2.5,2"/>
                  <path d="M3 14.5 L21 14.5" stroke="currentColor" stroke-width="1.2" stroke-dasharray="2.5,2"/>`,
  "niche":       `<path d="M3 6 L9 6 L9 14 L15 14 L15 6 L21 6" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/>
                  <path d="M3 18 L21 18" stroke="currentColor" stroke-width="1.2"/>`,
};

/** getSymbolDef (~50003–50008) — flat lookup across categories. */
export function getSymbolDef(key) {
  for (const cat of Object.keys(SYMBOL_LIBRARY)) {
    if (SYMBOL_LIBRARY[cat][key]) return SYMBOL_LIBRARY[cat][key];
  }
  return null;
}

/** The iframe's symbol id scheme (~50306). */
export function newSymbolId() {
  return "sym_" + Date.now() + "_" + Math.random().toString(36).slice(2, 7);
}

/** handleSymbolPlaceClick core (~50291–50313): gate to the wall rectangle, push the
 *  placement ({ id, wallId, key, u, h, label } — EXACT insertion order for serialization
 *  identity). NO history snapshot (the iframe doesn't push on place — LOCK-SL-08 covers
 *  delete only). Returns the placement or null when off-wall / unknown key. */
export function addSymbolPlacement(state, wall, key, u, h) {
  if (!wall || !getSymbolDef(key)) return null;
  const wallLen = Math.hypot(wall.end[0] - wall.start[0], wall.end[1] - wall.start[1]);
  const wallH = wallHeightOf(wall);
  if (u < 0 || u > wallLen || h < 0 || h > wallH) return null;
  const sp = { id: newSymbolId(), wallId: wall.id, key, u, h, label: "" };
  if (!state.symbolPlacements) state.symbolPlacements = [];
  state.symbolPlacements.push(sp);
  return sp;
}

/** Symbol delete (~50124–50129 / ~50348–50353): splice by id. Caller pushes history FIRST
 *  (LOCK-SL-08). Returns true when removed. */
export function removeSymbolPlacement(state, id) {
  const arr = (state && state.symbolPlacements) || [];
  const idx = arr.findIndex((p) => p.id === id);
  if (idx < 0) return false;
  arr.splice(idx, 1);
  return true;
}

/** Symbol props commits (~50112–50122): u clamps to [0, wallLen], h to ≥0, label raw.
 *  Values arrive in mm (the inputs), stored in metres. Mutates sp in place. */
export function symbolFieldCommit(sp, field, value, wallLen) {
  if (field === "u") sp.u = Math.max(0, Math.min(wallLen, (+value || 0) / 1000));
  else if (field === "h") sp.h = Math.max(0, (+value || 0) / 1000);
  else if (field === "label") sp.label = value;
  return sp;
}
